import { FormEvent, useEffect, useMemo, useState } from "react";
import { CalendarIcon, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import Logo from "../../components/Logo";
import NipDigitInput from "../../components/NipDigitInput";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { showToast } from "../../components/ui/toastHotSonner";
import { getSignupInfo, signup } from "../../api/authPatient";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const nipRegex = /^\d{10}$/;

/** Aligné sur `validatePasswordStrength` côté backend (voir docs/api/auth/PATIENT_SIGNUP_CLARIFICATION_FRONTEND.md). */
function validatePatientPassword(pw: string): string | null {
  if (pw.length < 8) return "Le mot de passe doit contenir au moins 8 caracteres.";
  if (!/[a-z]/.test(pw)) return "Le mot de passe doit contenir au moins une minuscule.";
  if (!/[A-Z]/.test(pw)) return "Le mot de passe doit contenir au moins une majuscule.";
  if (!/\d/.test(pw)) return "Le mot de passe doit contenir au moins un chiffre.";
  if (!/[@$!%*?&]/.test(pw)) {
    return "Le mot de passe doit contenir au moins un caractere special parmi : @ $ ! % * ? &";
  }
  return null;
}

export default function PatientSignup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [nip, setNip] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [dateNaissance, setDateNaissance] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [signupAllowed, setSignupAllowed] = useState(true);
  const [requirements, setRequirements] = useState<string[]>([
    "nip",
    "nom",
    "prenom",
    "date_naissance",
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const info = await getSignupInfo();
      if (!mounted) return;
      setLoadingInfo(false);
      if (!info.ok) {
        showToast(info.message || "Impossible de verifier les informations d'inscription.", "error");
        return;
      }
      setSignupAllowed(info.data.signupAllowed !== false);
      if (Array.isArray(info.data.requirements) && info.data.requirements.length > 0) {
        setRequirements(info.data.requirements);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedDateNaissance = useMemo(() => {
    if (!dateNaissance) return undefined;
    const parsed = parse(dateNaissance, "yyyy-MM-dd", new Date());
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [dateNaissance]);

  const requirementSet = useMemo(() => new Set(requirements), [requirements]);
  const canSubmit = !submitting && !loadingInfo && signupAllowed;

  const isRequired = (field: string) => requirementSet.has(field);

  const validateForm = (): string | null => {
    if (!emailRegex.test(email.trim())) return "Veuillez renseigner un email valide.";
    if (!password) return "Le mot de passe est requis.";
    const pwRule = validatePatientPassword(password);
    if (pwRule) return pwRule;
    if (confirmPassword !== password) return "Les mots de passe ne correspondent pas.";

    if (isRequired("nip") && !nipRegex.test(nip.trim())) {
      return "Le NIP doit contenir exactement 10 chiffres.";
    }
    if (isRequired("nom") && nom.trim().length < 2) return "Le nom doit contenir au moins 2 caracteres.";
    if (isRequired("prenom") && prenom.trim().length < 2) return "Le prenom doit contenir au moins 2 caracteres.";
    if (isRequired("date_naissance") && !dateNaissance) {
      return "La date de naissance est requise.";
    }
    return null;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    const validationError = validateForm();
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    setSubmitting(true);
    const result = await signup({
      email: email.trim(),
      password,
      confirmPassword,
      profileData: {
        nip: nip.trim(),
        nom: nom.trim(),
        prenom: prenom.trim(),
        date_naissance: `${dateNaissance}T00:00:00.000Z`,
      },
    });
    setSubmitting(false);

    if (!result.ok) {
      showToast(result.message || "Echec de l'inscription.", "error");
      return;
    }

    if (result.step === "VERIFY_EMAIL" && result.otpRequestId) {
      navigate("/patient/inscription/code", {
        replace: true,
        state: { otpRequestId: result.otpRequestId, email: email.trim() },
      });
      return;
    }

    navigate("/patient/connexion", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0B1F16]">
      <main className="flex-1 w-full max-w-md mx-auto flex flex-col items-center justify-center py-8 px-4">
        <div className="text-center">
          <Logo size={90} />
          <p className="mt-4 text-lg font-semibold text-[#0B1F16]">Creation de votre compte patient</p>
          <p className="mt-1 text-sm text-gray-600">Renseignez vos informations pour acceder a AGBAZA.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 w-full space-y-3">
          {!signupAllowed && !loadingInfo && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-2">
              L&apos;inscription en tant que patient n&apos;est pas autorisee pour le moment (inscriptions
              fermees cote serveur).
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-600">Email</label>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value.replace(/\s/g, ""))}
              className="w-full h-12 rounded-[10px] border px-3 bg-white"
              style={{ borderColor: "#E5E7EB" }}
              disabled={!canSubmit}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-600">NIP</label>
            <NipDigitInput
              value={nip}
              onChange={setNip}
              invalid={nip.length > 0 && !nipRegex.test(nip)}
              disabled={!canSubmit}
              validColor="#E5E7EB"
              boxClassName="h-12 w-12 text-center rounded-[10px] border px-1 bg-white text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30 disabled:opacity-60"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">Nom</label>
              <input
                value={nom}
                onChange={(event) => setNom(event.target.value)}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
                disabled={!canSubmit}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">Prenom</label>
              <input
                value={prenom}
                onChange={(event) => setPrenom(event.target.value)}
                className="w-full h-12 rounded-[10px] border px-3 bg-white"
                style={{ borderColor: "#E5E7EB" }}
                disabled={!canSubmit}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-600">Date de naissance</label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full h-12 rounded-[10px] border px-3 bg-white flex items-center justify-between text-left disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#00553E]/30"
                  style={{ borderColor: "#E5E7EB" }}
                  disabled={!canSubmit}
                >
                  <span className={selectedDateNaissance ? "text-[#0B1F16]" : "text-gray-400"}>
                    {selectedDateNaissance
                      ? format(selectedDateNaissance, "d MMMM yyyy", { locale: fr })
                      : "jj/mm/aaaa"}
                  </span>
                  <CalendarIcon size={18} className="text-gray-500" aria-hidden />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto">
                <Calendar
                  mode="single"
                  locale={fr}
                  selected={selectedDateNaissance}
                  defaultMonth={selectedDateNaissance ?? new Date(new Date().getFullYear() - 25, 0)}
                  captionLayout="dropdown"
                  startMonth={new Date(new Date().getFullYear() - 120, 0)}
                  endMonth={new Date()}
                  disabled={{ after: new Date() }}
                  onSelect={(date) => {
                    setDateNaissance(date ? format(date, "yyyy-MM-dd") : "");
                    setCalendarOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-600">Mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full h-12 rounded-[10px] border px-3 pr-12 bg-white"
                style={{ borderColor: "#E5E7EB" }}
                disabled={!canSubmit}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]/30 disabled:opacity-50"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                disabled={!canSubmit}
              >
                {showPassword ? <EyeOff size={20} aria-hidden /> : <Eye size={20} aria-hidden />}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              8 caracteres minimum : minuscule, majuscule, chiffre et un special parmi @ $ ! % * ? &
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-600">Confirmer le mot de passe</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full h-12 rounded-[10px] border px-3 pr-12 bg-white"
                style={{ borderColor: "#E5E7EB" }}
                disabled={!canSubmit}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]/30 disabled:opacity-50"
                aria-label={showConfirmPassword ? "Masquer la confirmation" : "Afficher la confirmation"}
                disabled={!canSubmit}
              >
                {showConfirmPassword ? <EyeOff size={20} aria-hidden /> : <Eye size={20} aria-hidden />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-12 rounded-[10px] font-semibold text-white cursor-pointer disabled:opacity-60"
            style={{ background: "#00553E" }}
          >
            {submitting ? "Inscription..." : "S'inscrire"}
          </button>

          <div className="text-sm text-[#0B1F16]">
            Vous avez deja un compte ?{" "}
            <Link className="font-semibold" style={{ color: "#00553E" }} to="/patient/connexion">
              Connectez-vous
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
